import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { feSidebarDetourReturn, feSidebarOpen } from 'actions/ui.actions';
import { fromPathnameToIsWeek } from 'bl/pathname.bl';
import LINK from 'constants/link.const';
import SP from 'constants/ui/sidePanel.const';
import { useNavFor } from 'hooks/useNav.hook';
import { selectSidebarAppointmentId, selectSidebarDetour, selectSidebarOpen } from 'selectors/sidebar.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import linkTo from 'utils/links/linkTo.util';
import isFullString from 'utils/predicates/string/isFullString.util';

/**
 * This component intercepts client profile routes and opens the new sidebar-based
 * client profile instead of rendering the old full-page client profile.
 * It dispatches the sidebar open action and lets the Scheduler + Sidebar handle rendering.
 *
 * Handles routes:
 * - /salon/:id/day/:dateString/client/:client_id
 * - /salon/:id/week/:dateString/:stylistId/client/:client_id
 * - /salon/:id/day/:dateString/client/edit/:client_id
 * - /salon/:id/week/:dateString/:stylistId/client/edit/:client_id
 * - /salon/:id/day/:dateString?/appointment/new/client/:client_id
 * - /salon/:id/day/:dateString?/appointment/edit/:appointmentId/client/:client_id
 * - /salon/:id/week/:dateString?/:stylistId/appointment/new/client/:client_id
 * - /salon/:id/week/:dateString?/:stylistId/appointment/edit/:appointmentId/client/:client_id
 *
 * When the sidebar closes, it navigates back to the base scheduler view without the /client/:id path.
 */
const CLIENT_SEGMENT_RE = /\/client\/(edit\/)?\d+$/;

export const ClientProfileRedirect = () => {
  const dispatch = useDispatch();
  const { client_id: clientIdFromUrl, id: salonId, dateString, stylistId } = useParams();
  const nav = useNavFor('ClientProfileRedirect');
  const location = useLocation();
  const hasOpenedSidebar = useRef(false);
  const hasSwitchedToEdit = useRef(false);
  const detourAppointmentId = useRef(null);
  const sidebarOpen = useSelector(selectSidebarOpen);
  const sidebarDetour = useSelector(selectSidebarDetour);
  const sidebarAppointmentId = useSelector(selectSidebarAppointmentId);
  const sidebarClientData = useSelector((state) => state?.sidebar?.clientProfile?.clientData);
  const prevSidebarOpen = useRef(sidebarOpen);
  const previousPath = useRef(null); // Store the path before /client/:id was added

  const clientId = fromAnyToNumber(clientIdFromUrl);
  const pathname = location?.pathname;
  const isWeek = fromPathnameToIsWeek(pathname);
  const isEditRoute = pathname.includes('/client/edit/');

  const handleSidebarOpen = useCallback((payload) => dispatch(feSidebarOpen(payload)), [dispatch]);
  const handleDetourReturn = useCallback(() => dispatch(feSidebarDetourReturn()), [dispatch]);

  // Reset flags when the clientId changes
  // This allows navigating to a different client profile
  useEffect(() => {
    hasOpenedSidebar.current = false;
    hasSwitchedToEdit.current = false;
    detourAppointmentId.current = null;
  }, [clientId]);

  useEffect(() => {
    // When arriving at the client profile route from a detour (appointment sidebar was open),
    // first return from the detour to restore the sidebar open state, then open the client profile
    if (clientId && !hasOpenedSidebar.current) {
      // Store the previous path (the route that led here, from location.state or by removing /client/:id)
      const fromPath = location?.state?.from;

      if (fromPath) {
        // Use the explicit path passed via navigation state
        previousPath.current = fromPath;
      } else {
        // Fallback: calculate by removing /client/:client_id from current path
        previousPath.current = pathname.replace(CLIENT_SEGMENT_RE, '');
      }

      // If we're coming from a detour (appointment sidebar closed with detour flag),
      // store the appointmentId so we can return to it, then restore sidebar open state
      if (sidebarDetour) {
        detourAppointmentId.current = sidebarAppointmentId;
        handleDetourReturn();
      }

      // Always open client overview panel
      handleSidebarOpen({
        panel: SP.clientOverview,
        clientId,
        fromAppointmentId: detourAppointmentId.current,
      });

      hasOpenedSidebar.current = true;
    }
  }, [clientId, handleSidebarOpen, handleDetourReturn, sidebarDetour, sidebarAppointmentId, location, pathname]);

  // Auto-switch to edit panel once client data has loaded (for /client/edit/ routes)
  useEffect(() => {
    if (isEditRoute && sidebarClientData && !hasSwitchedToEdit.current && hasOpenedSidebar.current) {
      hasSwitchedToEdit.current = true;
      handleSidebarOpen({ panel: SP.clientEdit });
    }
  }, [isEditRoute, sidebarClientData, handleSidebarOpen]);

  // Handle sidebar close by navigating back to the base scheduler view or appointment edit
  useEffect(() => {
    // Track previous and current sidebar state
    const wasOpen = prevSidebarOpen.current;
    const isClosed = !sidebarOpen;

    // All conditions that must be true to trigger navigation:
    // 1. Sidebar was previously open
    // 2. Sidebar is now closed
    // 3. Sidebar was opened by this component
    // 4. A valid clientId is present
    const shouldNavigateBack =
      wasOpen && // Sidebar was previously open
      isClosed && // Sidebar is now closed
      hasOpenedSidebar.current && // This component opened the sidebar
      clientId; // There is a valid clientId

    if (shouldNavigateBack) {
      let basePath;

      // First priority: use the stored previous path if available
      if (isFullString(previousPath.current)) {
        basePath = previousPath.current;
      } else {
        // Fallback: determine where to navigate based on the current route
        const isFromAppointmentRoute = isFullString(pathname) && pathname.includes('/appointment/');

        if (isFromAppointmentRoute) {
          // If coming from an appointment route, remove the /client/:client_id part
          // e.g., /salon/1/day/20240101/appointment/edit/123/client/456 -> /salon/1/day/20240101/appointment/edit/123
          basePath = pathname.replace(CLIENT_SEGMENT_RE, '');
        } else {
          // Navigate to base scheduler view
          const pattern = isWeek ? LINK.scheduleWeek : LINK.scheduleDay;
          basePath = linkTo({
            pattern,
            params: {
              salonId: fromAnyToTrimmed(salonId),
              dateString: fromAnyToTrimmed(dateString),
              stylistId: fromAnyToTrimmed(stylistId),
            },
            fault: '/',
          });
        }
      }

      // If we came from an appointment detour, reopen the appointment sidebar
      const returnToAppointmentId = detourAppointmentId.current;

      // Reset the refs for next time
      hasOpenedSidebar.current = false;
      previousPath.current = null;
      detourAppointmentId.current = null;

      // Navigate to base path if it's different from the current path
      if (isFullString(basePath) && basePath !== pathname) {
        nav.repl(basePath);
      }

      if (returnToAppointmentId) {
        handleSidebarOpen({ panel: SP.appointmentDefault, appointmentId: returnToAppointmentId });
      }
    }

    // Update previous sidebar state
    prevSidebarOpen.current = sidebarOpen;
  }, [sidebarOpen, clientId, pathname, isWeek, salonId, dateString, stylistId, nav, handleSidebarOpen]);

  return null;
};
